import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { approveUserInvite, getUserInvite } from "src/actions/usersActions";
import Loading from "src/components/Loading";
import Message from "src/components/Message";
import {
  confirmPasswordMessage,
  phoneMessage,
  requireMessage,
} from "src/utils/error-messages";
import { setToken } from "src/utils/storage";
import * as yup from "yup";

const RegisterUser = (props) => {
  const hash = useMemo(() => props.match.params.hash, [props]);
  const { loading } = useSelector((state) => state.users);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const schema = yup.object().shape({
    Fullname: yup.string().required(t(requireMessage)),
    Password: yup.string().required(t(requireMessage)),
    ConfirmPassword: yup
      .string()
      .required(t(requireMessage))
      .oneOf([yup.ref("Password"), null], t(confirmPasswordMessage)),
    Phone: yup
      .string()
      .required(t(requireMessage))
      .matches(/^([0-9]{12})$/, {
        message: t(phoneMessage),
        excludeEmptyString: true,
      }),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  let Scale = watch("Scale");

  const { is_authenticated, loading: authLoading } = useSelector(
    (state) => state.auth
  );

  const redirect = props.location.search
    ? props.location.search.split("=")[1]
    : "/";

  useEffect(() => {
    if (!is_authenticated) {
      dispatch(getUserInvite(hash))
        .then((res) => {
          setValue("Name", res.Company.Name);
          setValue("Email", res.Email);
          setValue("UserGroup", res.UserGroup.Name);
          setValue("Scale", res.Scale?.Name);
        })
        .catch((error) => {
          setError(error.data);
        });
    } else {
      props.history.push(redirect);
    }
  }, [hash, is_authenticated, props.history, redirect]);

  const onSubmit = (data) => {
    let formData = {
      Token: hash,
      Fullname: data.Fullname,
      Password: data.Password,
      Phone: data.Phone,
    };

    dispatch(approveUserInvite(formData)).then((res) => {
      setToken(res.Token);
      window.location.href = "/";
    });
  };

  return (
    <>
      {error && (
        <div className="p-4">
          <Message variant="danger" title={error.Status.Message} />
        </div>
      )}

      <div className="c-app c-default-layout flex-row align-items-center">
        {(authLoading || loading) && <Loading />}

        {!error && (
          <Container>
            <div className="d-flex align-items-center mb-5">
              <a href="/">
                <img
                  style={{ width: "60px" }}
                  src="/images/logo.png"
                  className="img-fluid"
                  alt="logo"
                />
              </a>

              <div className="mx-auto">
                <h2 className="text-center">
                  {t("register.userRegistration")}
                </h2>
                <span className="d-block text-center">
                  <span className="text-danger">*</span>{" "}
                  {t("register.asterisk")}
                </span>
              </div>
            </div>

            <div className="login__form">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="Name">{t("company.name")}</label>

                      <input
                        type="text"
                        id="Name"
                        {...register("Name")}
                        className="form-control custom-input"
                        readOnly
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="Email">{t("login.email")}</label>

                      <input
                        type="text"
                        id="Email"
                        {...register("Email")}
                        className="form-control custom-input"
                        readOnly
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="UserGroup">{t("user.role")}</label>

                      <input
                        type="text"
                        id="UserGroup"
                        {...register("UserGroup")}
                        className="form-control custom-input"
                        readOnly
                      />
                    </div>
                  </Col>

                  {Scale && (
                    <Col md={6}>
                      <div className="form-group">
                        <label htmlFor="Scale">{t("scale.default")}</label>

                        <input
                          type="text"
                          id="Scale"
                          {...register("Scale")}
                          className="form-control custom-input"
                          readOnly
                        />
                      </div>
                    </Col>
                  )}

                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="Fullname">
                        <span className="text-danger">*</span>{" "}
                        {t("user.fullname")}
                      </label>
                      <input
                        type="text"
                        id="Fullname"
                        {...register("Fullname")}
                        className="form-control custom-input"
                      />
                      {errors.Fullname && (
                        <span className="invalid-feedback d-block">
                          {errors.Fullname.message}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="Password">
                        <span className="text-danger">*</span>{" "}
                        {t("login.password")}
                      </label>
                      <input
                        type="password"
                        id="Password"
                        {...register("Password")}
                        className="form-control custom-input"
                      />
                      {errors.Password && (
                        <span className="invalid-feedback d-block">
                          {errors.Password.message}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="ConfirmPassword">
                        <span className="text-danger">*</span>{" "}
                        {t("login.confirmPassword")}
                      </label>
                      <input
                        type="password"
                        id="ConfirmPassword"
                        {...register("ConfirmPassword")}
                        className="form-control custom-input"
                      />
                      {errors.ConfirmPassword && (
                        <span className="invalid-feedback d-block">
                          {errors.ConfirmPassword.message}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label htmlFor="Phone">
                        <span className="text-danger">*</span> {t("user.phone")}
                      </label>

                      <input
                        type="text"
                        id="Phone"
                        {...register("Phone")}
                        className="form-control custom-input"
                      />
                      {errors.Phone ? (
                        <span className="invalid-feedback d-block">
                          {errors.Phone?.message}
                        </span>
                      ) : (
                        <small className="form-text text-muted">
                          ({t("utils.eg")} 994507776655)
                        </small>
                      )}
                    </div>
                  </Col>

                  <div className="d-flex justify-content-between align-items-center">
                    <button type="submit" className="btn btn-custom">
                      {t("register.default")}
                    </button>
                  </div>
                </Row>
              </form>
            </div>
          </Container>
        )}
      </div>
    </>
  );
};

export default RegisterUser;
